type ConnectionInitPayload = {
  type: 'connection.init';
  clientId: string;
  jwt: string;
};

type MessageSendPayload = {
  type: 'message.send';
  buildingId: string;
  text: string;
  clientMessageId: string;
  sessionId?: string;
};

type EventsResumePayload = {
  type: 'events.resume';
  sessionId: string;
  messageId: string;
  afterSeq: number;
};

type MessageCancelPayload = {
  type: 'message.cancel';
  sessionId: string;
  messageId: string;
};

export type RealtimeChatbotPayload =
  | ConnectionInitPayload
  | MessageSendPayload
  | EventsResumePayload
  | MessageCancelPayload;

export type RealtimeChatbotEvent = {
  type: string;
  seq?: number;
  sessionId?: string;
  messageId?: string;
  label?: string;
  content?: string;
  message?: string;
  [key: string]: any;
};

type RealtimeChatbotClientOptions = {
  websocketUrl: string;
};

type ConnectionCredentials = {
  clientId: string;
  jwt: string;
};

type Listener = Function;

type RealtimeSocket = {
  readyState: number;
  onopen: Function | null;
  onmessage: Function | null;
  onerror: Function | null;
  onclose: Function | null;
  send: Function;
  close: Function;
};

const WS_OPEN_STATE = 1;
const WS_CLOSED_STATE = 3;

class RealtimeChatbotClient {
  private websocketUrl: string;

  private socket: RealtimeSocket | null;

  private listeners: Set<Listener>;

  constructor({ websocketUrl }: RealtimeChatbotClientOptions) {
    this.websocketUrl = websocketUrl;
    this.socket = null;
    this.listeners = new Set();
  }

  connect(credentials: ConnectionCredentials): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        if (this.socket && this.socket.readyState === WS_OPEN_STATE) {
          resolve();
          return;
        }

        const WebSocketCtor = (globalThis as unknown as { WebSocket?: any }).WebSocket;
        if (!WebSocketCtor) {
          reject(new Error('Realtime websocket is unavailable in this environment.'));
          return;
        }

        this.socket = new WebSocketCtor(this.websocketUrl) as RealtimeSocket;

        this.socket.onopen = () => {
          this.send({
            type: 'connection.init',
            clientId: credentials.clientId,
            jwt: credentials.jwt,
          });
          resolve();
        };

        this.socket.onmessage = (messageEvent) => {
          this.emit(RealtimeChatbotClient.parseEvent(messageEvent.data));
        };

        this.socket.onerror = () => {
          reject(new Error('Realtime chatbot websocket failed to connect.'));
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  onEvent(listener: Listener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  setOnClose(handler: (() => void) | null) {
    if (!this.socket) return;
    this.socket.onclose = () => {
      handler?.();
    };
  }

  setOnError(handler: Function | null) {
    if (!this.socket) return;
    this.socket.onerror = (event) => {
      handler?.(event);
    };
  }

  send(payload: RealtimeChatbotPayload) {
    if (!this.socket || this.socket.readyState !== WS_OPEN_STATE) {
      throw new Error('Realtime chatbot websocket is not connected.');
    }
    this.socket.send(JSON.stringify(payload));
  }

  sendMessage(params: {
    buildingId: string;
    text: string;
    clientMessageId: string;
    sessionId?: string;
  }) {
    this.send({
      type: 'message.send',
      buildingId: params.buildingId,
      text: params.text,
      clientMessageId: params.clientMessageId,
      ...(params.sessionId ? { sessionId: params.sessionId } : {}),
    });
  }

  resumeEvents(params: { sessionId: string; messageId: string; afterSeq: number }) {
    this.send({
      type: 'events.resume',
      sessionId: params.sessionId,
      messageId: params.messageId,
      afterSeq: params.afterSeq,
    });
  }

  cancelMessage(params: { sessionId: string; messageId: string }) {
    this.send({
      type: 'message.cancel',
      sessionId: params.sessionId,
      messageId: params.messageId,
    });
  }

  disconnect() {
    if (this.socket) {
      this.socket.onopen = null;
      this.socket.onmessage = null;
      this.socket.onerror = null;
      this.socket.onclose = null;
      this.socket.close();
      this.socket = null;
    }
  }

  getReadyState() {
    return this.socket?.readyState ?? WS_CLOSED_STATE;
  }

  private emit(event: RealtimeChatbotEvent | null) {
    if (!event) return;
    this.listeners.forEach((listener) => {
      (listener as Function).call(null, event);
    });
  }

  private static parseEvent(rawData: unknown): RealtimeChatbotEvent | null {
    try {
      const parsed = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
      if (!parsed || typeof parsed !== 'object') return null;

      if (parsed.event && typeof parsed.event === 'object') {
        return parsed.event as RealtimeChatbotEvent;
      }

      if (parsed.type && typeof parsed.type === 'string') {
        return parsed as RealtimeChatbotEvent;
      }

      return null;
    } catch {
      return null;
    }
  }
}

export const createRealtimeChatbotClient = (options: RealtimeChatbotClientOptions) => (
  new RealtimeChatbotClient(options)
);

export default RealtimeChatbotClient;
